import { useMemo } from 'react';

import { sortByDate } from '@/entities/inbox/helpers';
import { useSJDatabase } from '@/shared/lib/sj-orm';
import { InboxDto } from '@/shared/lib/sj-orm/models/inbox.dto';
import { arrays } from '@/shared/utils/arrays';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';

import { FieldDate } from '../components/FieldDate';
import { FieldEmailInfo } from '../components/FieldEmailInfo';
import { FieldFiles } from '../components/FieldFiles';

export const useInboxEmailList = (isAttached: boolean, filter?: (item: InboxDto) => boolean) => {
  const { t } = useTranslation('entities/inbox');
  const sjdb = useSJDatabase();

  return useMemo(() => {
    if (!sjdb.collections?.inbox) {
      return null;
    }

    const columns = [
      {
        key: 'date',
        title: t('emails.columns.date'),
        render: (item: InboxDto) => <FieldDate date={item.createdAt} />,
      },
      {
        key: 'title',
        title: t('emails.columns.title'),
        render: (item: InboxDto) => <FieldEmailInfo email={item} />,
      },
      {
        key: 'file',
        title: t('emails.columns.files'),
        render: (item: InboxDto) => <FieldFiles files={item.attachments} />,
      },
      {
        key: 'sender',
        title: t('emails.columns.sender'),
        render: (item: InboxDto) => item.senderEmail,
      },
    ];

    const emails = sjdb.collections.inbox.findMany(
      (item) => Boolean(item.isEmailAttached) === Boolean(isAttached),
    );

    const filtered = filter ? emails.filter(filter) : emails;

    // TODO: replace sortByDate to common comparing func
    const items = arrays.toSorted(filtered, (f1, f2) => sortByDate(f1, f2) * -1);

    return { items, columns, hasData: emails.length > 0 };
  }, [sjdb, filter]);
};
